import { CreditCard, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { SaccoMetrics } from "@/hooks/sacco/useSaccoMetrics.ts";

interface LoanEligibilityCardProps {
  metrics: SaccoMetrics;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function LoanEligibilityCard({
  metrics,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: LoanEligibilityCardProps) {
  const { loanEligibility, outstandingLoanBalance, availableCredit } = metrics;
  const isAtCapacity = availableCredit <= 0;

  function convertAmount(raw: number): number {
    if (ratesError || ratesLoading || !convert || displayCurrency === nativeCurrency) return raw;
    try {
      return convert(raw, nativeCurrency, displayCurrency);
    } catch {
      return raw;
    }
  }

  const effectiveCurrency = ratesError ? nativeCurrency : displayCurrency;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Loan Eligibility</CardTitle>
        <CreditCard className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-2xl font-bold">
            {ratesLoading ? (
              <Skeleton className="h-8 w-32 inline-block" />
            ) : (
              formatCurrency(convertAmount(loanEligibility), effectiveCurrency)
            )}
          </p>
          <p className="text-xs text-muted-foreground">Maximum borrowing capacity</p>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Outstanding Loans</span>
            <span className="font-medium text-red-600">
              {ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmount(outstandingLoanBalance), effectiveCurrency)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Available Credit</span>
            <div className="flex items-center gap-2">
              {isAtCapacity && (
                <Badge variant="destructive" className="text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  At Capacity
                </Badge>
              )}
              <span className={`font-medium ${isAtCapacity ? "text-red-600" : "text-green-600"}`}>
                {ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmount(availableCredit), effectiveCurrency)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
